import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnInstance, TableSortByToggleProps } from 'react-table';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { Column } from '../../interfaces';
import { classes } from './table.style';

interface DraggableHeaderProps {
  column: ColumnInstance<any>;
  id: string;
  showHeaderTooltip: boolean;
}

export function DraggableHeader({
  column,
  id,
  showHeaderTooltip
}: DraggableHeaderProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'pointer'
  };

  const [dragging, setDragging] = React.useState(false);
  const startPosition = React.useRef<{ x: number; y: number } | null>(null);
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const distancePixels: number = 10;

  const handleMouseDown = (event: React.MouseEvent): void => {
    startPosition.current = { x: event.clientX, y: event.clientY };
    setDragging(false);
    headerRef.current = event.currentTarget as HTMLDivElement;
  };

  const handleMouseMove = (event: React.MouseEvent): void => {
    if (startPosition.current) {
      const distance =
        Math.abs(event.clientX - startPosition.current.x!) +
        Math.abs(event.clientY - startPosition.current.y!);

      if (distance > distancePixels) {
        setDragging(true);
      }
    }
  };

  const handleMouseUp = (event: React.MouseEvent): void => {
    if (!dragging && event.currentTarget === headerRef.current) {
      column.toggleSortBy?.();
    }
    startPosition.current = null;
    setDragging(false);
    headerRef.current = null;
  };

  const generateSortingIndicator = () => {
    return column.isSorted ? column.isSortedDesc ? <ArrowDropDown /> : <ArrowDropUp /> : '';
  };

  const tooltip = (column as Column<any>).headertooltip;
  const toggleProps: TableSortByToggleProps = {
    title: showHeaderTooltip ? (tooltip ?? column.Header?.toString()) : undefined
  };

  return (
    <div
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      style={style}
      className={`${classes.cell} ${isDragging ? 'dragging' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      {...toggleProps}
    >
      <span>{column.render('Header')}</span>
      {generateSortingIndicator()}
    </div>
  );
}
